/**
 * Gas.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { debugGas } from '../utils/debug.js'
import { PlanError, PLAN_ERROR_CODES } from './errors.js'

// Gas mix utilities
//
// ONE SINGLE PRESSURE SCALE, THE PHYSICAL ONE.
//
// Every depth <-> pressure conversion in this file (surfacePressure,
// pressureAtDepth, depthAtPressure, and their aliases atmosphericPressure and
// ambientPressure) describes the same real physics, with the very constants
// Subsurface uses. The decompression model and the gas density calculation
// therefore share one set of constants, and MV-Plan can be compared to
// Subsurface bar for bar.
export class Gas {
  // Nitrogen fraction of air, used by the EAD / END equivalences
  static AIR_N2_FRACTION = 0.79

  // Slack allowed when rounding a MOD down to whole meters, in meters.
  //
  // Diving practice names deco gases by round depths: pure O2 is "the 6 m gas"
  // and EAN50 "the 21 m gas". With the real water column, the exact MOD of pure
  // O2 at 1.6 bar is 5.81 m, which a plain truncation turns into 5 m: an
  // off-grid switch depth, and a number no diver would recognise. The same
  // slack is what Subsurface applies in dive::gas_mod, as (int)(depth / 3000 +
  // 0.1) on the 3 m stop grid, and it restores the depths MV-Plan displayed
  // before the move to real pressure (O2 at 6 m, air at 56 m).
  //
  // Cost: the depth kept is never more than MOD_TOLERANCE past the exact MOD,
  // so the ppO2 overshoot is bounded by MOD_TOLERANCE * specificWeight(water)
  // and therefore grows with the density the diver is now free to select.
  // Measured, at sea level: 1.6195 bar instead of 1.600 on pure O2 held at 6 m
  // in sea water, 1.6254 in the Red Sea, and 1.4010 instead of 1.400 on air at
  // 56 m. At SALINITY_MAX (12500 g/l) the bound is 0.0368 bar, and a sweep of
  // every whole-percent O2 fraction over 0 to 4000 m of altitude reaches
  // 1.6368 bar on a 1.6 limit (pure O2 at 8 m, 3670 m of altitude) - so at most
  // 1.637 bar on a 1.6 limit and 1.437 bar on a 1.4 limit. This is a deliberate
  // convention, not an accident of rounding.
  static MOD_TOLERANCE = 0.3

  // Pressure of one standard atmosphere, in bar. Subsurface core/units.h,
  // operator""_atm: pressure_t { .mbar = round(atm * 1013.25) }, and
  // dive::get_surface_pressure() falls back to 1_atm when the dive carries no
  // measured surface pressure.
  static SEA_LEVEL_PRESSURE = 1.01325

  // Water density, expressed the way Subsurface expresses it: a salinity in
  // grams per litre. Subsurface core/units.h:
  //   #define SEAWATER_SALINITY 10300    // 1.03 kg/l
  //   #define FRESHWATER_SALINITY 10000  // 1.00 kg/l
  static SEAWATER_SALINITY = 10300
  static FRESHWATER_SALINITY = 10000

  // Named water densities offered to the diver, in g/l, and the resulting
  // specific weight of the water column:
  //   fresh    10000 -> 0.098100 bar/m   lakes, quarries, flooded mines
  //   brackish 10150 -> 0.099572 bar/m   estuaries, brackish lagoons
  //   salt     10300 -> 0.101043 bar/m   average sea water (Subsurface default)
  //   red_sea  10400 -> 0.102024 bar/m   Red Sea and other salty basins
  // The two historical presets read their value from the constants above, so
  // 'salt' and 'fresh' keep exactly one definition in this file.
  static WATER_PRESETS = Object.freeze({
    fresh: Gas.FRESHWATER_SALINITY,
    brackish: 10150,
    salt: Gas.SEAWATER_SALINITY,
    red_sea: 10400
  })

  // Salinity range accepted by the planner, in g/l, both bounds included.
  // Deliberately wide: 9500 covers the least mineralised high altitude lakes,
  // 12500 lets the Dead Sea through (12400).
  //
  // Out of range is an error, never a silent clamp. A density typed in kg/l
  // (1.03 instead of 1030) or in the wrong decade would otherwise flatten the
  // water column - at 1.03 g/l, 40 m would weigh 0.0004 bar instead of 4.04 -
  // and produce a plan with no decompression stop at all for a real deco dive.
  static SALINITY_MIN = 9500
  static SALINITY_MAX = 12500

  // Dive site altitude range accepted by the planner, in metres, both bounds
  // included. -500 covers the Dead Sea shore (-430 m), the lowest water on
  // Earth; 9000 is past any diveable altitude and only exists to catch a value
  // typed in feet (a 4000 m lake entered as 13123).
  static ALTITUDE_MIN = -500
  static ALTITUDE_MAX = 9000

  // Standard gravity (9.81 m/s2) folded into the unit conversion, and the
  // divisor that turns a salinity in the unit above into a specific weight.
  // Subsurface core/dive.cpp, salinity_to_specific_weight():
  //   return salinity * 0.981 / 100000.0;
  // The result is in mbar per mm, which is numerically the same figure as bar
  // per metre: 0.101043 bar/m in sea water, 0.098100 bar/m in fresh water.
  // The multiplication is written in Subsurface's own order because the two
  // orderings do not give the same last bit in fresh water.
  static GRAVITY_FACTOR = 0.981
  static SPECIFIC_WEIGHT_DIVISOR = 100000

  // Barometric scale height in metres: P = P0 * exp(-altitude / 8435).
  // MV-Plan's own constant, kept unchanged. Subsurface takes the surface
  // pressure as a direct input to its computation (diveplan::surface_pressure)
  // and only needs the altitude law for display, where core/units.h
  // altitude_to_pressure() uses a coarser 7800 m scale height.
  static BAROMETRIC_SCALE_HEIGHT = 8435

  constructor(fO2 = 0.21, fHe = 0.00, name = null) {
    Gas.validate({ fO2, fHe, name })
    this.fO2 = fO2
    this.fHe = fHe
    this.fN2 = 1 - fO2 - fHe
    this.name = name || this.autoName()
  }

  // Fraction consistency tolerance: absorbs the binary rounding of fractions
  // entered as integer percents (1 - 0.32 - 0 is 0.6799999999999999, not 0.68)
  static FRACTION_TOLERANCE = 1e-9

  // Validate any gas-like object: store literals are plain objects, not Gas
  // instances, so validation cannot live in the constructor only.
  // Throws PlanError(plan_error_invalid_gas) with the faulty values.
  // Returns true when the gas is usable.
  static validate(gas) {
    if (!gas || typeof gas !== 'object') {
      console.error('Gas.validate: gas is not an object', gas)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_GAS, {
        name: undefined, fO2: undefined, fHe: undefined, reason: 'not_an_object'
      })
    }

    const name = gas.name
    const fO2 = gas.fO2
    // fHe is optional everywhere in the codebase, absent means 0
    const fHe = (gas.fHe === undefined || gas.fHe === null) ? 0 : gas.fHe

    const fail = (reason) => {
      console.error('Gas.validate: invalid gas (%s) name=%o fO2=%o fHe=%o', reason, name, fO2, fHe)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_GAS, { name, fO2, fHe, reason })
    }

    if (typeof fO2 !== 'number' || !Number.isFinite(fO2)) fail('fO2_not_finite')
    if (fO2 <= 0) fail('fO2_too_low')
    if (fO2 > 1) fail('fO2_too_high')

    if (typeof fHe !== 'number' || !Number.isFinite(fHe)) fail('fHe_not_finite')
    if (fHe < 0) fail('fHe_too_low')
    if (fHe >= 1) fail('fHe_too_high')

    // Tolerance absorbs the rounding of fractions entered as integer percents
    if (fO2 + fHe > 1 + Gas.FRACTION_TOLERANCE) fail('fractions_sum_above_one')

    // fN2 is optional (Gas.normalizeMix computes it), but when it is carried by
    // the object it is the value the Buhlmann model actually breathes: an
    // absent-but-declared, negative or inconsistent fN2 under-loads the tissues
    // and produces a plan with far too little decompression.
    if (gas.fN2 !== undefined && gas.fN2 !== null) {
      const fN2 = gas.fN2
      if (typeof fN2 !== 'number' || !Number.isFinite(fN2)) {
        console.error('Gas.validate: invalid gas (fN2_not_finite) name=%o fO2=%o fHe=%o fN2=%o', name, fO2, fHe, fN2)
        throw new PlanError(PLAN_ERROR_CODES.INVALID_GAS, { name, fO2, fHe, fN2, reason: 'fN2_not_finite' })
      }
      if (fN2 < 0) {
        console.error('Gas.validate: invalid gas (fN2_negative) name=%o fO2=%o fHe=%o fN2=%o', name, fO2, fHe, fN2)
        throw new PlanError(PLAN_ERROR_CODES.INVALID_GAS, { name, fO2, fHe, fN2, reason: 'fN2_negative' })
      }
      const expectedFN2 = 1 - fO2 - fHe
      if (Math.abs(fN2 - expectedFN2) > Gas.FRACTION_TOLERANCE) {
        console.error('Gas.validate: invalid gas (fN2_inconsistent) name=%o fO2=%o fHe=%o fN2=%o expected=%o',
          name, fO2, fHe, fN2, expectedFN2)
        throw new PlanError(PLAN_ERROR_CODES.INVALID_GAS, { name, fO2, fHe, fN2, expectedFN2, reason: 'fN2_inconsistent' })
      }
    }

    // A gas may reach validate() unnamed: Gas.normalizeMix then generates the
    // display name before the mix is used by the planner. A declared name must
    // however not be blank.
    if (name !== undefined && name !== null) {
      if (typeof name !== 'string' || name.trim() === '') fail('empty_name')
    }

    return true
  }

  // Validate then complete a gas-like object for the planner: guarantees a
  // finite fHe, the fN2 the model will breathe, and an exploitable name.
  // Store gases are plain literals, frequently without fN2 and sometimes
  // without name; feeding them raw to the planner gives NaN tissue loadings
  // (missing fN2) or defeats the name-based gas switch (missing name).
  // pureO2Label = false for a CCR diluent (see formatMixName).
  // Returns a new object, the caller's literal is never mutated.
  static normalizeMix(gas, pureO2Label = true) {
    Gas.validate(gas)
    const fO2 = gas.fO2
    const fHe = (gas.fHe === undefined || gas.fHe === null) ? 0 : gas.fHe
    // validate() guarantees the name is either absent (undefined / null) or a
    // non blank string, so `??` is enough to tell "no name" from "a real name"
    return {
      ...gas,
      fO2,
      fHe,
      fN2: 1 - fO2 - fHe,
      name: gas.name ?? Gas.formatMixName(fO2, fHe, pureO2Label)
    }
  }

  // Defensive guard on the tissue loading path. Buhlmann.loadTissues multiplies
  // fN2 / fHe by the ambient pressure without checking them, so a single non
  // finite fraction poisons the 16 compartments, makes every ceiling 0 and
  // returns a plan with no decompression stop at all - silently.
  // Only finiteness and sign are checked: CCR equivalent gases are normalized
  // against different pressures and legitimately do not sum to 1.
  // Throws PlanError(plan_error_invalid_gas) after logging.
  static assertBreathingMix(mix, context) {
    const fail = (reason, values) => {
      console.error('%s: refusing to load tissues with an invalid mix (%s) %o', context, reason, values)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_GAS, { context, reason, ...values })
    }

    if (!mix || typeof mix !== 'object') fail('mix_not_an_object', { mix })

    const fN2 = mix.fN2
    const fHe = (mix.fHe === undefined || mix.fHe === null) ? 0 : mix.fHe
    const fO2 = mix.fO2

    if (typeof fN2 !== 'number' || !Number.isFinite(fN2)) fail('fN2_not_finite', { name: mix.name, fO2, fN2, fHe })
    if (fN2 < 0) fail('fN2_negative', { name: mix.name, fO2, fN2, fHe })
    if (typeof fHe !== 'number' || !Number.isFinite(fHe)) fail('fHe_not_finite', { name: mix.name, fO2, fN2, fHe })
    if (fHe < 0) fail('fHe_negative', { name: mix.name, fO2, fN2, fHe })
    if (fO2 !== undefined && fO2 !== null && (typeof fO2 !== 'number' || !Number.isFinite(fO2))) {
      fail('fO2_not_finite', { name: mix.name, fO2, fN2, fHe })
    }

    return true
  }

  autoName() {
    return Gas.formatAutoName(this.fO2, this.fHe)
  }

  // Shared implementation of the automatic display names.
  // pureO2Label = true  -> 100% O2 is displayed 'O2' (open circuit bottles)
  // pureO2Label = false -> 100% O2 is displayed 'EAN100' (CCR diluent, which
  //                        is never pure oxygen: keeping the EANxx form avoids
  //                        suggesting a valid pure O2 diluent)
  static formatMixName(fO2, fHe = 0, pureO2Label = true) {
    const o2 = Math.round(fO2 * 100)
    const he = Math.round((fHe || 0) * 100)

    if (he > 0) return `TX${o2}/${he}`
    if (o2 === 21) return 'Air'
    if (pureO2Label && o2 === 100) return 'O2'
    return `EAN${o2}`
  }

  // Generate automatic display name from O2/He fractions
  // Format: "TX12/35" (trimix), "EAN32" (nitrox), "Air", "O2"
  static formatAutoName(fO2, fHe = 0) {
    return Gas.formatMixName(fO2, fHe, true)
  }

  // Generate automatic display name for a diluent (no pure O2 shortcut)
  // Format: "TX12/35" (trimix), "Air", "EAN32"
  static formatAutoNameDiluent(fO2, fHe = 0) {
    return Gas.formatMixName(fO2, fHe, false)
  }

  // Water density of the dive site, in g/l.
  //
  // waterType accepts two forms, and only these two:
  //   - a WATER_PRESETS key: 'fresh', 'brackish', 'salt', 'red_sea'
  //   - a number, the density in g/l, checked against SALINITY_MIN / MAX
  // An absent value (undefined or null) means average sea water, which is the
  // historical default of every conversion in this file.
  //
  // Anything else is logged then refused with PlanError(plan_error_invalid_config).
  // Falling back to sea water on an unusable input is exactly what must not
  // happen: the diver would be shown a plan computed for another site than the
  // one selected, and in fresh water that plan is too short.
  static waterDensity(waterType) {
    if (waterType === undefined || waterType === null) return Gas.SEAWATER_SALINITY

    const fail = (reason) => {
      console.error('Gas.waterDensity: unusable water type (%s) value=%o, expected one of %o or a density in g/l within [%o, %o]',
        reason, waterType, Object.keys(Gas.WATER_PRESETS), Gas.SALINITY_MIN, Gas.SALINITY_MAX)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_CONFIG, {
        context: 'Gas.waterDensity',
        setting: 'waterType',
        value: waterType,
        reason,
        presets: Object.keys(Gas.WATER_PRESETS),
        min: Gas.SALINITY_MIN,
        max: Gas.SALINITY_MAX
      })
    }

    if (typeof waterType === 'string') {
      // An own-property test and not a plain lookup: WATER_PRESETS inherits
      // from Object.prototype, so waterType = 'constructor' would otherwise
      // return a function and turn every pressure of the plan into NaN.
      // hasOwnProperty.call rather than Object.hasOwn: the build declares no
      // target, so Vite compiles for its default baseline (Safari 14), and
      // Object.hasOwn only exists from Safari 15.4 - it is a runtime method,
      // which Vite does not polyfill.
      if (!Object.prototype.hasOwnProperty.call(Gas.WATER_PRESETS, waterType)) fail('unknown_preset')
      return Gas.WATER_PRESETS[waterType]
    }

    if (typeof waterType !== 'number' || !Number.isFinite(waterType)) fail('not_a_density')
    if (waterType < Gas.SALINITY_MIN || waterType > Gas.SALINITY_MAX) fail('density_out_of_range')

    return waterType
  }

  // Specific weight of the water column, in bar per metre.
  // Fresh water 0.098100 bar/m, sea water 0.101043 bar/m, Red Sea 0.102024 bar/m.
  // Subsurface core/dive.cpp, salinity_to_specific_weight().
  // The multiplication keeps Subsurface's own operand order, which is what
  // makes the fresh water figure land on the same last bit.
  static specificWeight(waterType = 'salt') {
    return Gas.waterDensity(waterType) * Gas.GRAVITY_FACTOR / Gas.SPECIFIC_WEIGHT_DIVISOR
  }

  // Dive site altitude in metres, validated. Counterpart of waterDensity():
  // the two halves of the environment now get the same treatment.
  //
  // An absent altitude means sea level. Anything else has to be a finite number
  // inside the bounds, and is refused otherwise instead of being coerced.
  // `altitude || 0` used to swallow NaN silently, so an emptied input field
  // planned the dive at sea level with no log at all - the worst possible
  // failure for a mountain lake dive, where the plan comes out too short.
  static altitudeMeters(altitude) {
    if (altitude === undefined || altitude === null || altitude === '') return 0
    const value = typeof altitude === 'string' ? Number(altitude) : altitude
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      console.error('Gas.altitudeMeters: altitude is not a finite number: %o', altitude)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_CONFIG, {
        setting: 'altitude', value: altitude, reason: 'not_a_number'
      })
    }
    if (value < Gas.ALTITUDE_MIN || value > Gas.ALTITUDE_MAX) {
      console.error('Gas.altitudeMeters: altitude %o m out of range [%o, %o]',
        value, Gas.ALTITUDE_MIN, Gas.ALTITUDE_MAX)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_CONFIG, {
        setting: 'altitude', value, min: Gas.ALTITUDE_MIN, max: Gas.ALTITUDE_MAX, reason: 'out_of_range'
      })
    }
    return value
  }

  // Surface pressure at a given altitude, in bar.
  // 1.01325 bar at sea level (one standard atmosphere), barometric decay above.
  static surfacePressure(altitude = 0) {
    return Gas.SEA_LEVEL_PRESSURE * Math.exp(-Gas.altitudeMeters(altitude) / Gas.BAROMETRIC_SCALE_HEIGHT)
  }

  // Ambient pressure at depth, in bar
  // env = { altitude: <metres>, waterType: <preset key or density in g/l> },
  // both optional; see waterDensity() for the accepted water types
  static pressureAtDepth(depth, env = {}) {
    return Gas.surfacePressure(env.altitude) + depth * Gas.specificWeight(env.waterType)
  }

  // Depth corresponding to an ambient pressure, in metres
  static depthAtPressure(pressure, env = {}) {
    return (pressure - Gas.surfacePressure(env.altitude)) / Gas.specificWeight(env.waterType)
  }

  // Maximum Operating Depth at given ppO2, never negative
  static calcMOD(fO2, ppO2Max = 1.4, env = {}) {
    if (typeof fO2 !== 'number' || !Number.isFinite(fO2) || fO2 <= 0 || fO2 > 1) {
      console.error('Gas.calcMOD: invalid fO2=%o (ppO2Max=%o)', fO2, ppO2Max)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_GAS, { fO2, ppO2Max, reason: 'invalid_fO2' })
    }
    return Math.max(0, Math.floor(Gas.depthAtPressure(ppO2Max / fO2, env) + Gas.MOD_TOLERANCE))
  }

  // Equivalent Air Depth (for N2 narcosis)
  // Expanded form of depthAtPressure(pressureAtDepth(depth) * fN2 / 0.79),
  // written this way to stay bit-exact with the historical sea level formula
  static calcEAD(depth, fN2, env = {}) {
    const surfaceOffset = Gas.surfacePressure(env.altitude) * Gas.depthPerBar(env.waterType)
    return Math.round((depth + surfaceOffset) * fN2 / Gas.AIR_N2_FRACTION - surfaceOffset)
  }

  // Equivalent Narcotic Depth (N2 only - O2 is not narcotic)
  static calcEND(depth, fO2, fHe, env = {}) {
    const fN2 = 1 - fO2 - fHe
    const surfaceOffset = Gas.surfacePressure(env.altitude) * Gas.depthPerBar(env.waterType)
    return Math.round((depth + surfaceOffset) * fN2 / Gas.AIR_N2_FRACTION - surfaceOffset)
  }

  // Depth at which a mix gives a wanted ppO2, in metres, exact and unrounded.
  //
  // The inverse of ppO2(): pAmb = ppO2 / fO2, then the depth that pressure sits
  // at. Written here rather than in a view because the pressure scale, the water
  // and the altitude belong to this file - a helper computing 51 m at sea level
  // would be 3 m wrong on a mountain lake.
  //
  // The result may be negative, which is the honest answer when the target is
  // below what the mix already gives at the surface: pure oxygen never reads
  // 1.0 bar underwater, it reads 1.013 bar the moment it is breathed at sea
  // level. The caller decides what to do with that, and must not round it away.
  static depthForPpO2(fO2, ppO2, env = {}) {
    if (typeof fO2 !== 'number' || !Number.isFinite(fO2) || fO2 <= 0 || fO2 > 1) {
      console.error('Gas.depthForPpO2: invalid fO2=%o (ppO2=%o)', fO2, ppO2)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_GAS, { fO2, ppO2, reason: 'invalid_fO2' })
    }
    if (typeof ppO2 !== 'number' || !Number.isFinite(ppO2) || ppO2 <= 0) {
      console.error('Gas.depthForPpO2: invalid target ppO2=%o (fO2=%o)', ppO2, fO2)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_GAS, { fO2, ppO2, reason: 'invalid_ppO2' })
    }
    return Gas.depthAtPressure(ppO2 / fO2, env)
  }

  // Depth at which a mix gives a wanted equivalent narcotic depth, in metres,
  // exact and unrounded. The inverse of calcEND(), minus its rounding.
  //
  // Returns null for a mix carrying no nitrogen: a heliox has an END of zero at
  // every depth, so the question has no answer rather than a wrong one. Same for
  // a target below the surface offset, which no depth reaches.
  static depthForEND(end, fO2, fHe = 0, env = {}) {
    if (typeof end !== 'number' || !Number.isFinite(end) || end < 0) {
      console.error('Gas.depthForEND: invalid target END=%o', end)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_GAS, { end, fO2, fHe, reason: 'invalid_end' })
    }
    const fN2 = 1 - fO2 - fHe
    if (!Number.isFinite(fN2) || fN2 <= 0) {
      console.error('Gas.depthForEND: the mix carries no nitrogen (fO2=%o, fHe=%o), its END is zero everywhere', fO2, fHe)
      return null
    }
    const surfaceOffset = Gas.surfacePressure(env.altitude) * Gas.depthPerBar(env.waterType)
    return (end + surfaceOffset) * Gas.AIR_N2_FRACTION / fN2 - surfaceOffset
  }

  // Partial pressure of O2 at depth
  static ppO2(fO2, depth, env = {}) {
    return fO2 * Gas.pressureAtDepth(depth, env)
  }

  // Partial pressure of N2 at depth
  static ppN2(fN2, depth, env = {}) {
    return fN2 * Gas.pressureAtDepth(depth, env)
  }

  // Partial pressure of He at depth
  static ppHe(fHe, depth, env = {}) {
    return fHe * Gas.pressureAtDepth(depth, env)
  }

  // ppO2 limit a gas may be breathed at, in bar.
  //
  // The stricter of the two limits the diver set: the one carried by the gas
  // (Gases view, "ppO2 max" field) and the one of the phase being planned
  // (Settings, working or deco). Both are his and both mean something - the
  // per gas field caps a bottle he does not want pushed, the phase setting
  // caps the whole plan - so a mix is usable only where it satisfies the two.
  // The engine used to read the phase limit alone, and the interface the gas
  // limit alone, which is how a plan could print "EAN32 (MOD 40m)" next to a
  // table breathing something else entirely at 40 m.
  static ppO2LimitOf(gas, ppO2Max = 1.4) {
    const gasLimit = gas?.ppO2Max
    const usableGasLimit = Number.isFinite(gasLimit) && gasLimit > 0
    const usablePhaseLimit = Number.isFinite(ppO2Max) && ppO2Max > 0
    if (!usableGasLimit) return ppO2Max
    if (!usablePhaseLimit) return gasLimit
    return Math.min(gasLimit, ppO2Max)
  }

  // Depth the planner may hold a gas down to, in metres.
  // An explicit `mod` on the gas wins: it is a depth the diver wrote himself.
  static modOf(gas, ppO2Max = 1.4, env = {}) {
    if (gas?.mod !== undefined) return gas.mod
    return Gas.calcMOD(gas?.fO2, Gas.ppO2LimitOf(gas, ppO2Max), env)
  }

  // Whether a gas may be breathed at this depth, i.e. depth is within its MOD.
  static reachesDepth(gas, depth, ppO2Max = 1.4, env = {}) {
    return depth <= Gas.modOf(gas, ppO2Max, env)
  }

  // Gas kept out of the plan: disabled by the diver. A gas that carries no
  // `enabled` field at all is a plain mix literal and counts as available.
  static isEnabled(gas) {
    return Boolean(gas?.enabled) || gas?.enabled === undefined
  }

  // Best gas for depth: the richest enabled mix whose MOD reaches it.
  //
  // When no gas reaches the depth, the fallback matters as much as the choice
  // itself, and it used to be `gases[0]` - the order the diver happened to
  // type his gases in, disabled ones included. On a bailout list starting with
  // O2, a loop lost at 40 m was therefore planned on pure oxygen at 4.94 bar.
  // The fallback is now the least hyperoxic enabled gas, the one whose MOD is
  // the deepest: still a mix held past its MOD, but the only survivable one of
  // the list. The planner turns that same condition into a plan warning
  // (DivePlanner.gasAboveMODWarning), because a console line reaches nobody.
  static bestGas(gases, depth, ppO2Max = 1.4, env = {}) {
    const enabledGases = gases.filter(g => Gas.isEnabled(g))
    const usableGases = enabledGases.filter(g => Gas.reachesDepth(g, depth, ppO2Max, env))
    const best = [...usableGases].sort((a, b) => b.fO2 - a.fO2)[0]
    if (best) {
      debugGas('bestGas: depth=%dm, ppO2Max=%o -> %s (from %d usable gases)',
        depth, ppO2Max, best.name, usableGases.length)
      return best
    }

    // Deepest MOD first, which for a given ppO2 limit is the leanest mix.
    // Sorting on the MOD and not on fO2 alone keeps the per gas ppO2 limits in
    // the comparison: a lean mix the diver capped low may well stop shallower
    // than a richer one he cleared to 1.6.
    const fallback = [...enabledGases]
      .sort((a, b) => Gas.modOf(b, ppO2Max, env) - Gas.modOf(a, ppO2Max, env))[0]
    if (!fallback) {
      console.error('Gas.bestGas: no enabled gas at all among %d for %om (ppO2Max=%o), falling back on the first of the list (%o)',
        gases.length, depth, ppO2Max, gases[0]?.name)
      return gases[0]
    }
    console.error('Gas.bestGas: no enabled gas reaches %om (ppO2Max=%o), falling back on the least hyperoxic one: %s, MOD %om, ppO2 %o bar there',
      depth, ppO2Max, fallback.name, Gas.modOf(fallback, ppO2Max, env),
      Gas.ppO2(fallback.fO2, depth, env).toFixed(2))
    return fallback
  }

  // Depth of the next gas switch below the current depth.
  // Returns the deepest MOD among the gases not yet usable at `depth`,
  // or 0 when no gas is concerned.
  static nextGasStop(gases, depth, ppO2Max = 1.4, env = {}) {
    // Same MOD as bestGas, per gas limit included: the depth the ascent stops
    // at to switch has to be the depth the switch is then allowed at.
    const usableGases = gases.filter(g => Gas.isEnabled(g) && !Gas.reachesDepth(g, depth, ppO2Max, env))
    debugGas('nextGasStop: from %d usable gases', usableGases.length)

    let nextStop = 0
    for (const gas of usableGases) {
      const gasMod = Gas.modOf(gas, ppO2Max, env)
      if (gasMod > nextStop) nextStop = gasMod
    }

    debugGas('nextGasStop: depth=%dm, nextStop=%dm, ppO2Max=%o (from %d usable gases)',
      depth, nextStop, ppO2Max, usableGases.length)
    return nextStop
  }

  // NOAA single exposure limits of the central nervous system oxygen clock:
  // [ppO2 in bar, minutes to 100 %]. NOAA Diving Manual, table of oxygen
  // partial pressure and exposure time limits for working divers, the same
  // rows dive computers and Subsurface (core/divelist.cpp, cns_table) carry.
  // Below 0.5 bar the clock does not run.
  static CNS_TABLE = Object.freeze([
    [0.5, Infinity],
    [0.6, 720],
    [0.7, 570],
    [0.8, 450],
    [0.9, 360],
    [1.0, 300],
    [1.1, 240],
    [1.2, 210],
    [1.3, 180],
    [1.4, 150],
    [1.5, 120],
    [1.6, 45]
  ])

  // CNS toxicity per minute at a given ppO2, in percent per minute: 100 over
  // the NOAA limit, interpolated linearly between two rows of CNS_TABLE.
  //
  // This used to be a step function, one rate per 0.1 bar bucket, whose top
  // bucket was wrong: 1.00 %/min up to 1.6 bar where the NOAA limit is 45 min
  // (2.22 %/min), then a flat 3.00 %/min above. EAN50 at 21 m (1.57 bar) was
  // charged half of what the table says, and pure O2 at 6 m (1.62 bar, the
  // depth MOD_TOLERANCE deliberately keeps) three times the EAN50 rate for
  // 0.05 bar more. The rate is interpolated rather than the limit: 100/limit
  // is convex, so this lands on the safe side between two rows.
  //
  // Above 1.6 bar NOAA publishes nothing. The slope of the last two rows is
  // carried on (about 13.9 %/min per bar): continuous at 1.6 bar, 2.5 %/min at
  // 1.62 bar, 3.6 %/min at 1.7 bar, 7.8 %/min at 2.0 bar. A convention, kept
  // steep on purpose: a plan spending time there is already flagged as
  // hyperoxic by alerts.js, and its CNS must not read as comfortable.
  static cnsPerMinute(ppO2) {
    if (!Number.isFinite(ppO2) || ppO2 <= Gas.CNS_TABLE[0][0]) return 0
    const rate = limit => (limit === Infinity ? 0 : 100 / limit)
    const table = Gas.CNS_TABLE
    for (let i = 1; i < table.length; i++) {
      const [pHigh, limitHigh] = table[i]
      if (ppO2 <= pHigh) {
        const [pLow, limitLow] = table[i - 1]
        return rate(limitLow) + (rate(limitHigh) - rate(limitLow)) * (ppO2 - pLow) / (pHigh - pLow)
      }
    }
    const [pPrev, limitPrev] = table[table.length - 2]
    const [pLast, limitLast] = table[table.length - 1]
    const slope = (rate(limitLast) - rate(limitPrev)) / (pLast - pPrev)
    return rate(limitLast) + slope * (ppO2 - pLast)
  }

  // OTU (Oxygen Toxicity Units) per minute at given ppO2
  // Formula: OTU = t * ((ppO2 - 0.5) / 0.5)^0.83
  // Only accumulates above 0.5 bar ppO2
  static otuPerMinute(ppO2) {
    if (ppO2 <= 0.5) return 0
    return Math.pow((ppO2 - 0.5) / 0.5, 0.83)
  }

  // Calculate total OTU for a segment
  static calcOTU(ppO2, timeMinutes) {
    return Gas.otuPerMinute(ppO2) * timeMinutes
  }

  // Calculate total CNS for a segment
  static calcCNS(ppO2, timeMinutes) {
    return Gas.cnsPerMinute(ppO2) * timeMinutes
  }

  // Half time of the CNS oxygen clock at the surface, in minutes.
  // NOAA recommendation, and the value dive computers use (Shearwater,
  // Subsurface): the CNS percentage accumulated by a dive halves every 90 min
  // spent out of the water.
  static CNS_HALF_TIME = 90

  // CNS percentage left of `cns` after `surfaceMinutes` at the surface.
  //
  // Only the CNS decays. OTU has no comparable recovery model in use: the
  // accepted practice is to add up the units over the day against a 300 OTU
  // limit, so the planner carries the OTU total forward untouched rather than
  // invent a decay that would understate the exposure.
  static residualCns(cns, surfaceMinutes) {
    if (!Number.isFinite(cns) || cns < 0) {
      console.error('residualCns: invalid CNS (%o), expected a finite value >= 0, treating it as 0', cns)
      return 0
    }
    if (!Number.isFinite(surfaceMinutes) || surfaceMinutes < 0) {
      console.error('residualCns: invalid surface interval (%o), expected a finite duration >= 0, keeping the CNS as it is', surfaceMinutes)
      return cns
    }
    const residual = cns * Math.pow(0.5, surfaceMinutes / Gas.CNS_HALF_TIME)
    debugGas('residualCns: %o%% after %o min at the surface -> %o%%', cns, surfaceMinutes, residual)
    return residual
  }

  // Gas density at depth (g/L)
  // Used to assess breathing resistance and CO2 retention risk
  static molarVolume(tempCelsius = 20) {
    return 22.414 * (tempCelsius + 273.15) / 273.15
  }

  // Atmospheric pressure at altitude, in bar.
  // Alias of surfacePressure(): now that the whole file runs on the physical
  // pressure scale, the atmospheric pressure the gas density needs and the
  // surface pressure the decompression model needs are the same quantity.
  // Kept public so existing callers keep working.
  static atmosphericPressure(altitudeMeters = 0) {
    return Gas.surfacePressure(altitudeMeters)
  }

  // Metres of water column per bar, derived from the specific weight above.
  // Fresh water 10.194 m/bar, sea water 9.897 m/bar, Red Sea 9.802 m/bar.
  // Kept public: CCR.js and the expanded calcEAD / calcEND formulas above
  // express the surface offset as surfacePressure * depthPerBar.
  static depthPerBar(waterType = 'salt') {
    return 1 / Gas.specificWeight(waterType)
  }

  // Ambient pressure at depth, in bar.
  // Alias of pressureAtDepth() with the historical positional signature.
  static ambientPressure(depth, waterType = 'salt', altitudeMeters = 0) {
    return Gas.pressureAtDepth(depth, { waterType, altitude: altitudeMeters })
  }

  // Gas density at depth (g/L)
  // Used to assess breathing resistance and CO2 retention risk
  // Formula: density = molarMass * pAmb / molarVolume
  // gasTemp: temperature of gas in lungs (~35°C), not water temperature
  static gasDensity(fO2, fN2, fHe, depth, environment = {}) {
    const { gasTemp = 35, waterType = 'salt', altitude = 0 } = environment
    const pAmb = Gas.ambientPressure(depth, waterType, altitude)
    const molarMass = fO2 * 32 + fN2 * 28 + fHe * 4
    const molarVol = Gas.molarVolume(gasTemp)
    return (molarMass * pAmb) / molarVol
  }
}
